interface Tier {
  id: string
  tier: number
  xp_required: number
  free_reward_type: string | null
  free_reward_value: string | null
  premium_reward_type: string | null
  premium_reward_value: string | null
}

interface Props {
  tier: Tier
  currentTier: number
  hasPremiumPass: boolean
  themeColor: string
  onClaim: (tierId: string) => void
}

const REWARD_ICON: Record<string, string> = {
  atlas_coins: '⚡',
  shards:      '💎',
  creature:    '🐉',
  skin:        '🎨',
  title:       '📜',
  token:       '🎫',
  banner:      '🚩',
  emote:       '😄',
  evolution:   '⬆️',
}

function RewardBadge({ type, value, locked }: { type: string | null; value: string | null; locked?: boolean }) {
  if (!type) return <span className="text-xs opacity-30">—</span>
  const icon = REWARD_ICON[type] ?? '🎁'
  const label = type === 'atlas_coins' ? `${value} ⚡`
    : type === 'shards' ? `${value} 💎`
    : value?.replace(/-/g, ' ') ?? type
  return (
    <span className={`text-xs font-display font-bold ${locked ? 'opacity-30' : 'text-ab-text'}`}>
      {icon} {label}
    </span>
  )
}

export default function TierRewardCard({ tier, currentTier, hasPremiumPass, themeColor, onClaim }: Props) {
  const reached = currentTier >= tier.tier
  const isNext = currentTier + 1 === tier.tier

  return (
    <div
      className={`rounded-lg p-3 flex items-center gap-3 transition-all ${
        reached ? 'opacity-100' : isNext ? 'opacity-70' : 'opacity-40'
      }`}
      style={{
        background: reached ? `${themeColor}10` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${reached ? `${themeColor}40` : 'rgba(255,255,255,0.06)'}`,
      }}
    >
      {/* Tier number */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-display font-black text-xs"
        style={{
          background: reached ? themeColor : 'rgba(255,255,255,0.08)',
          color: reached ? '#020510' : 'rgba(255,255,255,0.4)',
        }}
      >
        {tier.tier}
      </div>

      {/* Free reward */}
      <div className="flex-1 min-w-0">
        <p className="text-label text-xs opacity-50 mb-0.5">FREE</p>
        <RewardBadge type={tier.free_reward_type} value={tier.free_reward_value} />
      </div>

      {/* Premium reward */}
      <div className="flex-1 min-w-0 text-right">
        <p className="text-label text-xs opacity-50 mb-0.5">PREMIUM</p>
        <RewardBadge
          type={tier.premium_reward_type}
          value={tier.premium_reward_value}
          locked={!hasPremiumPass}
        />
      </div>

      {/* Claim button */}
      {reached && (
        <button
          onClick={() => onClaim(tier.id)}
          className="btn-primary text-xs px-3 py-1.5 flex-shrink-0 ml-2"
          style={{ background: themeColor, borderColor: themeColor, color: '#020510' }}
        >
          Claim
        </button>
      )}
    </div>
  )
}
